import React, { useState, useRef, useEffect, useMemo } from 'react';
import { ImageOff } from 'lucide-react';

interface LazyImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
    fallbackSrc?: string;
}

// Helper to generate responsive srcset for external image URLs
const generateResponsiveSrcset = (src: string): { srcSet?: string; sizes?: string } => {
    if (!src) return {};

    // Handle Unsplash URLs - they support w= parameter
    if (src.includes('unsplash.com')) {
        const tiny = src.replace(/w=\d+/, 'w=300').replace(/q=\d+/, 'q=70');
        const small = src.replace(/w=\d+/, 'w=400').replace(/q=\d+/, 'q=70');
        const medium = src.replace(/w=\d+/, 'w=600').replace(/q=\d+/, 'q=75');
        const large = src.replace(/w=\d+/, 'w=800').replace(/q=\d+/, 'q=80');
        return {
            srcSet: `${tiny} 300w, ${small} 400w, ${medium} 600w, ${large} 800w`,
            sizes: '(max-width: 480px) 300px, (max-width: 640px) 400px, (max-width: 1024px) 600px, 800px'
        };
    }

    // Handle Pexels URLs - they support w= parameter
    if (src.includes('pexels.com')) {
        const tiny = src.replace(/w=\d+/, 'w=300').replace(/q=\d+/, 'q=70');
        const small = src.replace(/w=\d+/, 'w=400').replace(/q=\d+/, 'q=70');
        const medium = src.replace(/w=\d+/, 'w=600').replace(/q=\d+/, 'q=75');
        const large = src.replace(/w=\d+/, 'w=800').replace(/q=\d+/, 'q=80');
        return {
            srcSet: `${tiny} 300w, ${small} 400w, ${medium} 600w, ${large} 800w`,
            sizes: '(max-width: 480px) 300px, (max-width: 640px) 400px, (max-width: 1024px) 600px, 800px'
        };
    }

    return {};
};

const LazyImage: React.FC<LazyImageProps> = ({ src, alt, className, fallbackSrc, srcSet, sizes, ...props }) => {
    const [error, setError] = useState(false);
    const [loaded, setLoaded] = useState(false);
    const imgRef = useRef<HTMLImageElement>(null);

    // Generate responsive srcset if not provided
    const responsiveProps = useMemo(() => {
        if (srcSet) return { srcSet, sizes }; // Use provided values
        return generateResponsiveSrcset(src || '');
    }, [src, srcSet, sizes]);

    useEffect(() => {
        // Reset states when src changes
        setError(false);
        setLoaded(false);

        // Check if image is already cached/loaded
        if (imgRef.current?.complete && imgRef.current?.naturalWidth > 0) {
            setLoaded(true);
        }
    }, [src]);

    const handleError = () => {
        setError(true);
        setLoaded(true); // Stop showing spinner on error
    };

    const handleLoad = () => {
        setLoaded(true);
    };

    // Show error state
    if (error && !fallbackSrc) {
        return (
            <div className={`flex flex-col items-center justify-center bg-gray-100 text-gray-400 ${className}`} title="Image not available">
                <ImageOff className="w-8 h-8 opacity-50" />
            </div>
        );
    }

    // Simple approach: just show the image directly, let browser handle loading
    return (
        <img
            ref={imgRef}
            src={error && fallbackSrc ? fallbackSrc : src}
            alt={alt || ''}
            className={`${className || ''}`}
            onError={handleError}
            onLoad={handleLoad}
            loading="lazy"
            decoding="async"
            {...responsiveProps}
            {...props}
        />
    );
};

export default LazyImage;

